import { createGrid, type PixelRect } from '@kioskforge/layout';
import type { Block, Capabilities, Layout } from '@kioskforge/shared';

/**
 * A5b.10 — the preview's device variants and their panel geometry.
 *
 * This is a *preview-only* subset of f10: it renders a static, non-interactive
 * picture of a compiled layout at a device's real pixel size, reusing the same
 * grid maths the player and the ESP32 verifier use (`packages/layout`), so block
 * edges agree with what a device would draw. It never fetches anything.
 */
export const DEVICE_PANELS = {
  'tablet-landscape': { deviceClass: 'tablet', width: 1280, height: 800, orientation: 'landscape' },
  'tablet-portrait': { deviceClass: 'tablet', width: 800, height: 1280, orientation: 'portrait' },
  esp32: { deviceClass: 'esp32', width: 800, height: 480, orientation: 'landscape' }
} as const;

export type DeviceVariant = keyof typeof DEVICE_PANELS;
export type PanelGeometry = (typeof DEVICE_PANELS)[DeviceVariant];

/**
 * The capability profile a preview assumes for a device class — the mirror of
 * what a real device claims on pairing, and the same list the deployment gate is
 * handed. Kept here (frontend-owned) so the preview and the admin's "RUNS ON"
 * panel cannot drift apart.
 */
export function deviceCapabilities(deviceClass: 'tablet' | 'esp32'): Capabilities {
  return {
    device_class: deviceClass,
    lockdown: 'pwa',
    touch: true,
    screen_w: deviceClass === 'esp32' ? 800 : 1280,
    screen_h: deviceClass === 'esp32' ? 480 : 800,
    blocks: deviceClass === 'esp32'
      ? ['media', 'text', 'buttons', 'rating', 'live_tile']
      : ['media', 'text', 'buttons', 'form', 'consent', 'rating', 'live_tile', 'web_embed'],
    app_version: '0.1.0'
  };
}

export type PlacedBlock = { block: Block; rect: PixelRect };

/** The screen to preview: an explicit id, else the layout's home, else the first. */
export function previewScreen(layout: Layout, screenId?: string): Layout['screens'][number] | undefined {
  const wanted = screenId ?? layout.home;
  return layout.screens.find((screen) => screen.id === wanted) ?? layout.screens[0];
}

/** One screen's blocks placed in a variant's panel pixels (rounded, shared maths). */
export function screenRects(layout: Layout, screenId: string, variant: DeviceVariant): PlacedBlock[] {
  const panel = DEVICE_PANELS[variant];
  const screen = layout.screens.find((item) => item.id === screenId);
  if (!screen) return [];
  const grid = createGrid(layout.grid, { width: panel.width, height: panel.height });
  return screen.blocks.map((block) => ({ block, rect: grid.rectOf(block.rect) }));
}
